import TextField from '@mui/material/TextField';
import { Box, InputAdornment, styled, TextFieldProps } from '@mui/material';
import { Controller, FieldValues } from 'react-hook-form';
import { filledInputClasses } from '@mui/material/FilledInput';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { StyledErrorText, StyledHelperTextWrapper, StyledIconWarning, StyledLabel } from 'redesign';
import { FormInputProps } from './form-input.types';

const LwFormInput = <ControlFields extends FieldValues = FieldValues>({
  name,
  label,
  control,
  rules,
  inputType = 'text',
  disabled,
  placeholder,
  helperText,
  onChange,
  ...props
}: FormInputProps<ControlFields>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const message = error ? error.message : helperText;
        return (
          <Box width="100%">
            <StyledTextField
              {...field}
              id={name}
              title={label}
              label={<StyledLabel required={!!rules?.required}>{label}</StyledLabel>}
              value={field.value ?? ''}
              type={inputType}
              variant="filled"
              InputLabelProps={{ shrink: true }}
              fullWidth
              placeholder={placeholder}
              error={!!error}
              InputProps={{
                endAdornment: error ? (
                  <InputAdornment position="end">
                    <StyledIconWarning />
                  </InputAdornment>
                ) : null,
              }}
              disabled={disabled}
              aria-required={!!rules?.required}
              {...props}
              onChange={(e) => {
                field.onChange(e);
                if (onChange) {
                  onChange(e);
                }
              }}
            />
            <StyledHelperTextWrapper className="error-text-wrapper">
              {message && (
                <StyledErrorText className={error ? 'error-state' : ''}>{message}</StyledErrorText>
              )}
            </StyledHelperTextWrapper>
          </Box>
        );
      }}
    />
  );
};

const StyledTextField = styled(TextField)<
  TextFieldProps & { flatLeft?: boolean; flatRight?: boolean }
>(({ theme, flatLeft, flatRight }) => ({
  [`& .${filledInputClasses.root}`]: {
    border: `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
    borderRadius: theme.spacing(3),
    ...{ ...(flatLeft && { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }) },
    ...{
      ...(flatRight && { borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0 }),
    },
    '&:hover, &.Mui-focused': {
      borderColor: theme.palette.lwPrimaryAccentLight[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
    },
    '&.Mui-error': {
      borderColor: theme.palette.lwDanger[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwDanger[20]}`,
    },
  },
  [`& .${inputAdornmentClasses.root}`]: {
    marginRight: theme.spacing(3),
  },
  [`& .${inputBaseClasses.input}`]: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}));

export { LwFormInput };
